// Summary Report (Success Metrics) — superadmin-only, top-level page with its own
// club selector ("All clubs" = id 0). See SUMMARY_REPORT_PLAN.md.
//
// Metrics 2-4 are percentages the server returns as named raw counts (client
// computes %); metric 5 (Closed-Loop Evidence) is an on-demand report download.
// "All" selections (club / offer / result) are sent to the server as integer 0.
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Box, Paper, Typography, Grid, Card, CardContent, Button, TextField, MenuItem,
    Autocomplete, Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
    TableSortLabel, LinearProgress, CircularProgress, Tooltip, IconButton, Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useUrlContext } from '../context/urlContext';
import { errorContext } from '../Components/ErrorDisplay';

// --- Metric definitions. Each metric names its two server counts (denominator
//     A, numerator B); the percentage B/A is computed client-side. ---
const METRICS = [
    {
        key: 'walletPassAdoption',
        label: 'Wallet Pass Adoption',
        definition: 'Percentage of successfully contacted members who add the pass to Apple/Google Wallet. Denominator = delivered SMS/email, not the total member base.',
        denom: { key: 'passCount', label: 'Passes Delivered' },
        numer: { key: 'walletPasses', label: 'Passes added to wallet' },
        urlMethod: 'walletPassAdoptionUrl',
        needsOffer: false,
        target: 30,
    },
    {
        key: 'activePassUsage',
        label: 'Active Pass Usage',
        definition: 'Percentage of pass holders who present the pass at least once (scan/tap) during the selected window.',
        denom: { key: 'passHolders', label: 'Pass holders' },
        numer: { key: 'scannedAtLeastOnce', label: 'Scanned at least once' },
        urlMethod: 'activePassUsageUrl',
        needsOffer: false,
    },
    {
        key: 'offerRedemptionReach',
        label: 'Offer Redemption Reach',
        definition: 'Percentage of pass holders who redeem at least one offer (unique redeemers ÷ pass holders).',
        // Same server key as Active Pass Usage, but shown as "Coupon holders" here
        // — only this metric's label changed; Active Pass Usage keeps "Pass holders".
        denom: { key: 'passHolders', label: 'Coupon holders' },
        numer: { key: 'scannedAtLeastOnce', label: 'Redeemed ≥1 offer' },
        urlMethod: 'offerRedemptionReachUrl',
        needsOffer: false,
    },
    {
        key: 'offerRedemptionEfficiency',
        label: 'Offer Redemption Efficiency',
        definition: 'Redemptions ÷ offers issued/loaded to pass.',
        denom: { key: 'offerIssued', label: 'Offers issued' },
        numer: { key: 'redemptions', label: 'Redemptions' },
        urlMethod: 'offerRedemptionEfficiencyUrl',
        needsOffer: true,
    },
];

const ALL_CLUB = { id: 0, name: 'All clubs' };
const ALL_OFFER = { id: 0, name: 'All offers' };
const RESULT_OPTIONS = [
    { value: 0, label: 'All' },
    { value: 1, label: 'Success' },
    { value: 2, label: 'Failure' },
];

// Format a Date as "YYYY-MM-DD" using local date parts (no timezone shift).
function ymd(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// Percentage B/A as a whole number. Denominator 0 (no data / not run yet) → 0,
// so the default zero-state shows "0%" and 0 counts.
function pct(numer, denom) {
    return denom > 0 ? Math.round((numer / denom) * 100) : 0;
}
function fmtPct(p) {
    return `${p}%`;
}

const emptyMetricState = () => ({ loading: false, error: false, results: [] });

export default function SummaryReport() {
    const { UrlHelperObj } = useUrlContext();
    const { handleError, notify } = useContext(errorContext);

    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);

    const [clubs, setClubs] = useState([ALL_CLUB]);
    const [selectedClub, setSelectedClub] = useState(ALL_CLUB);
    const [startDate, setStartDate] = useState(fourWeeksAgo);
    const [endDate, setEndDate] = useState(today);

    const [offers, setOffers] = useState([ALL_OFFER]);
    const [offer4Id, setOffer4Id] = useState(0);           // metric 4 offer

    const [metricData, setMetricData] = useState({
        walletPassAdoption: emptyMetricState(),
        activePassUsage: emptyMetricState(),
        offerRedemptionReach: emptyMetricState(),
        offerRedemptionEfficiency: emptyMetricState(),
    });
    const [hasRun, setHasRun] = useState(false);
    const [sort, setSort] = useState({ key: null, dir: 'asc' });

    const isAllClubs = selectedClub.id === 0;
    const datesValid = startDate && endDate && !Number.isNaN(startDate.getTime())
        && !Number.isNaN(endDate.getTime()) && startDate <= endDate;

    // --- Load the club list once. "All clubs" is prepended. ---
    useEffect(() => {
        axios.get(UrlHelperObj.partnerUrl())
            .then(res => res.data)
            .then(rows => {
                const list = Array.isArray(rows)
                    ? rows.map(c => ({ id: c.id, name: c.name })) : [];
                setClubs([ALL_CLUB, ...list]);
            })
            .catch(err => handleError(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Offers depend on the club: only non-membership promotions are offers.
    //     For "All clubs", the offer is forced to All (0) and pickers disabled. ---
    useEffect(() => {
        setOffer4Id(0);
        if (isAllClubs) {
            setOffers([ALL_OFFER]);
            return;
        }
        axios.get(UrlHelperObj.promotionUrl_GET(selectedClub.id, 1))
            .then(res => res.data)
            .then(rows => {
                const list = Array.isArray(rows)
                    ? rows.filter(p => Number(p.type) !== 0).map(p => ({ id: p.id, name: p.name }))
                    : [];
                setOffers([ALL_OFFER, ...list]);
            })
            .catch(err => handleError(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClub]);

    // --- Fetch a single metric (POST, JSON body). Stores its own loading/error
    //     so one metric failing never blocks the others. ---
    const fetchMetric = useCallback(async (metric, offerForEfficiency) => {
        setMetricData(prev => ({ ...prev, [metric.key]: { ...prev[metric.key], loading: true, error: false } }));
        const body = {
            issuerId: selectedClub.id,
            startDate: ymd(startDate),
            endDate: ymd(endDate),
        };
        if (metric.needsOffer) {
            body.offerId = isAllClubs ? 0 : offerForEfficiency;
        }
        try {
            const res = await axios.post(UrlHelperObj[metric.urlMethod](), body);
            const data = res.data || {};
            if (Number(data.error) === 1) {
                setMetricData(prev => ({ ...prev, [metric.key]: { loading: false, error: true, results: [] } }));
                return;
            }
            const results = Array.isArray(data.data) ? data.data : [];
            setMetricData(prev => ({ ...prev, [metric.key]: { loading: false, error: false, results } }));
        } catch (err) {
            setMetricData(prev => ({ ...prev, [metric.key]: { loading: false, error: true, results: [] } }));
            handleError(err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClub, startDate, endDate, isAllClubs, UrlHelperObj, handleError]);

    function handleRun() {
        if (!datesValid) {
            notify('Please choose a valid date range (start on or before end).', 'warning');
            return;
        }
        setHasRun(true);
        METRICS.forEach(m => fetchMetric(m, offer4Id));
    }

    // Changing the efficiency offer re-runs only that metric (once a run has happened).
    function handleOffer4Change(e) {
        const value = Number(e.target.value);
        setOffer4Id(value);
        if (hasRun && datesValid) {
            fetchMetric(METRICS.find(m => m.needsOffer), value);
        }
    }

    function handleClear() {
        setSelectedClub(ALL_CLUB);
        setStartDate(fourWeeksAgo);
        setEndDate(today);
        setOffers([ALL_OFFER]);
        setOffer4Id(0);
        setMetricData({
            walletPassAdoption: emptyMetricState(),
            activePassUsage: emptyMetricState(),
            offerRedemptionReach: emptyMetricState(),
            offerRedemptionEfficiency: emptyMetricState(),
        });
        setHasRun(false);
        setSort({ key: null, dir: 'asc' });
    }

    // ---- helpers to read a metric row's numbers ----
    function cellNumbers(metric, row) {
        return {
            numer: Number(row?.[metric.numer.key]) || 0,
            denom: Number(row?.[metric.denom.key]) || 0,
        };
    }

    // Build matrix rows from the full club list so every club is visible with 0
    // values before Run; cells fill from each metric's results once loaded.
    function buildMatrix() {
        const realClubs = clubs.filter(c => c.id !== 0);
        // Per-metric lookup: issuerId -> { numer, denom }.
        const lookups = {};
        METRICS.forEach(m => {
            const map = {};
            (metricData[m.key].results || []).forEach(row => {
                map[row.issuerId] = cellNumbers(m, row);
            });
            lookups[m.key] = map;
        });
        let rows = realClubs.map(c => {
            const cells = {};
            METRICS.forEach(m => { cells[m.key] = lookups[m.key][c.id] || { numer: 0, denom: 0 }; });
            return { issuerId: c.id, clubName: c.name, cells };
        });
        if (sort.key) {
            rows.sort((a, b) => {
                const pa = pct(a.cells[sort.key].numer, a.cells[sort.key].denom);
                const pb = pct(b.cells[sort.key].numer, b.cells[sort.key].denom);
                return sort.dir === 'asc' ? pa - pb : pb - pa;
            });
        } else {
            rows.sort((a, b) => a.clubName.localeCompare(b.clubName));
        }
        const totals = {};
        METRICS.forEach(m => {
            let numer = 0, denom = 0;
            rows.forEach(r => { numer += r.cells[m.key].numer; denom += r.cells[m.key].denom; });
            totals[m.key] = { numer, denom };
        });
        return { rows, totals };
    }

    const anyLoading = METRICS.some(m => metricData[m.key].loading);

    function handleSort(key) {
        setSort(prev => (prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'desc' }));
    }

    // ---------- render helpers ----------
    const infoIcon = (definition) => (
        <Tooltip title={definition} arrow enterTouchDelay={0} leaveTouchDelay={4000}>
            <IconButton size="small" aria-label="metric definition" sx={{ p: 0.25, ml: 0.5 }}>
                <InfoOutlinedIcon fontSize="inherit" color="action" />
            </IconButton>
        </Tooltip>
    );

    // A thin vertical line drawn over a progress bar at the target percentage.
    function TargetMarker({ target }) {
        const left = Math.min(Math.max(target, 0), 100);
        return (
            <Tooltip arrow title={`Target ${target}%`}>
                <Box sx={{
                    position: 'absolute', top: -1, bottom: -1, left: `${left}%`,
                    width: '2px', bgcolor: 'text.primary', opacity: 0.6, borderRadius: 1,
                }} />
            </Tooltip>
        );
    }

    // Pass/fail chip for a metric that carries a target (green if met, amber if below).
    function TargetChip({ p, target }) {
        const met = p >= target;
        return (
            <Box component="span" sx={{
                px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700,
                lineHeight: 1.4, whiteSpace: 'nowrap',
                color: met ? 'success.dark' : 'warning.dark',
                bgcolor: met ? 'success.light' : 'warning.light',
            }}>
                {met ? `≥${target}% ✓` : `<${target}% ✗`}
            </Box>
        );
    }

    function ResultBar({ numer, denom, target }) {
        const p = pct(numer, denom);
        const hasTarget = target != null;
        return (
            <Box sx={{ minWidth: 150 }}>
                <Typography component="span" sx={{
                    fontWeight: 600, mr: 1,
                    color: hasTarget ? (p >= target ? 'success.main' : 'warning.main') : 'inherit',
                }}>{fmtPct(p)}</Typography>
                <Typography component="span" variant="body2" color="text.secondary">
                    ({numer} / {denom})
                </Typography>
                <Box sx={{ position: 'relative', mt: 0.5 }}>
                    <LinearProgress
                        variant="determinate"
                        value={p === null ? 0 : Math.min(p, 100)}
                        sx={{ height: 6, borderRadius: 3 }}
                    />
                    {hasTarget && <TargetMarker target={target} />}
                </Box>
            </Box>
        );
    }

    // ---------- Mode A: single-club detail cards ----------
    function renderCards() {
        return (
            <Grid container spacing={2}>
                {METRICS.map(metric => {
                    const state = metricData[metric.key];
                    const row = state.results[0];
                    const { numer, denom } = cellNumbers(metric, row);
                    const p = pct(numer, denom);
                    return (
                        <Grid item xs={12} sm={6} md={3} key={metric.key}>
                            <Card variant="outlined" sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{metric.label}</Typography>
                                        {infoIcon(metric.definition)}
                                    </Box>

                                    {metric.needsOffer && (
                                        <TextField
                                            select size="small" fullWidth label="Offer"
                                            value={offer4Id} onChange={handleOffer4Change}
                                            disabled={isAllClubs}
                                            sx={{ mb: 1.5 }}
                                        >
                                            {offers.map(o => (
                                                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                            ))}
                                        </TextField>
                                    )}

                                    {state.loading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                                            <CircularProgress size={20} />
                                            <Typography variant="body2" color="text.secondary">Loading…</Typography>
                                        </Box>
                                    ) : state.error ? (
                                        <Typography color="error" variant="body2">
                                            Failed to load. <Button size="small" onClick={() => fetchMetric(metric, offer4Id)}>Retry</Button>
                                        </Typography>
                                    ) : (
                                        <>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="h4" sx={{
                                                    fontWeight: 700,
                                                    color: metric.target != null ? (p >= metric.target ? 'success.main' : 'warning.main') : 'inherit',
                                                }}>{fmtPct(p)}</Typography>
                                                {metric.target != null && <TargetChip p={p} target={metric.target} />}
                                            </Box>
                                            <Box sx={{ position: 'relative', my: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={p === null ? 0 : Math.min(p, 100)}
                                                    sx={{ height: 8, borderRadius: 4 }}
                                                />
                                                {metric.target != null && <TargetMarker target={metric.target} />}
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">{metric.denom.label}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{denom}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">{metric.numer.label}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{numer}</Typography>
                                            </Box>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        );
    }

    // ---------- Mode B: all-clubs success matrix ----------
    function renderMatrix() {
        const { rows, totals } = buildMatrix();
        return (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Club</TableCell>
                            {METRICS.map(m => (
                                <TableCell key={m.key} sx={{ fontWeight: 700 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TableSortLabel
                                            active={sort.key === m.key}
                                            direction={sort.key === m.key ? sort.dir : 'desc'}
                                            onClick={() => handleSort(m.key)}
                                        >
                                            {m.label}
                                        </TableSortLabel>
                                        {infoIcon(m.definition)}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && !anyLoading && (
                            <TableRow>
                                <TableCell colSpan={METRICS.length + 1}>
                                    <Typography variant="body2" color="text.secondary">No clubs to display.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map(r => (
                            <TableRow key={r.issuerId} hover>
                                <TableCell>{r.clubName}</TableCell>
                                {METRICS.map(m => {
                                    const state = metricData[m.key];
                                    const c = r.cells[m.key];
                                    return (
                                        <TableCell key={m.key}>
                                            {state.loading ? (
                                                <CircularProgress size={16} />
                                            ) : state.error ? (
                                                <Typography variant="body2" color="error">failed</Typography>
                                            ) : c ? (
                                                <Tooltip arrow title={`${m.numer.label}: ${c.numer} · ${m.denom.label}: ${c.denom}`}>
                                                    <Box><ResultBar numer={c.numer} denom={c.denom} target={m.target} /></Box>
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                        {rows.length > 0 && (
                            <TableRow sx={{ '& td': { borderTop: '2px solid rgba(0,0,0,0.2)', fontWeight: 600 } }}>
                                <TableCell sx={{ fontWeight: 700 }}>ALL (total)</TableCell>
                                {METRICS.map(m => {
                                    const t = totals[m.key];
                                    return (
                                        <TableCell key={m.key}>
                                            <Tooltip arrow title={`${m.numer.label}: ${t.numer} · ${m.denom.label}: ${t.denom}`}>
                                                <Box><ResultBar numer={t.numer} denom={t.denom} target={m.target} /></Box>
                                            </Tooltip>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Paper sx={{ p: '8px 12px', mb: 2 }}>
                <Typography variant="h5" sx={{ textAlign: 'left' }}>Summary Report</Typography>
            </Paper>

            {/* Filter bar — universal inputs (drive every metric) */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <Autocomplete
                            options={clubs}
                            value={selectedClub}
                            getOptionLabel={(o) => o.name || ''}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            disableClearable
                            onChange={(e, val) => setSelectedClub(val || ALL_CLUB)}
                            renderInput={(params) => <TextField {...params} label="Club" size="small" />}
                        />
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid item xs={6} sm={4} md={2}>
                            <DatePicker
                                label="Start date"
                                value={startDate}
                                inputFormat="dd/MM/yyyy"
                                maxDate={endDate || undefined}
                                onChange={setStartDate}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth InputLabelProps={{ shrink: true }} />}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <DatePicker
                                label="End date"
                                value={endDate}
                                inputFormat="dd/MM/yyyy"
                                minDate={startDate || undefined}
                                onChange={setEndDate}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth InputLabelProps={{ shrink: true }} />}
                            />
                        </Grid>
                    </LocalizationProvider>
                    <Grid item xs={12} sm={12} md={5}>
                        <Button variant="contained" color="primary" onClick={handleRun} disabled={!datesValid || anyLoading} sx={{ mr: 1 }}>
                            Run
                        </Button>
                        <Button variant="outlined" color="secondary" onClick={handleClear}>
                            Clear
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Metrics 2-4 — always visible so the metrics are clear; show 0 until Run loads values. */}
            <Box sx={{ mb: 2 }}>
                {!hasRun && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Showing 0 until you click <strong>Run</strong> to load values for the selected club and dates.
                    </Typography>
                )}
                {isAllClubs ? renderMatrix() : renderCards()}
            </Box>

        </Box>
    );
}
